import {
  AlertCircle,
  Ban,
  Banknote,
  CreditCard,
  Download,
  ExternalLink,
  LogIn,
  LogOut,
  Mail,
  MessageCircle,
  Plus,
  Printer,
  QrCode,
  Receipt,
  RefreshCw,
  Save,
  Send,
  Smartphone,
  Tag,
  Trash2,
} from "lucide-react";
import type { DetailedHelpMap, HelpContext } from "./types";

/**
 * Ajuda detalhada: modal de detalhe de uma reserva já existente (aberto a
 * partir da lista de /reservas), conta e pagamentos (folio/split), Hóspedes,
 * Portal dos Hóspedes (visão da própria propriedade). Conteúdo verificado
 * lendo cada componente real antes de escrever. O modal chama
 * useHelpScope("detalhe.<aba>") enquanto está montado — as chaves aqui usam
 * "/reservas#detalhe.info" e "/reservas#detalhe.folio" pra mostrar só a aba
 * que está de fato aberta, em vez de misturar com a lista de reservas.
 *
 * Quase todo item aqui tem `domKey`: no detalhe da reserva os botões nascem
 * e somem conforme o status (check-in só existe em reserva confirmada,
 * check-out só com hóspede hospedado, assinatura só se há FNRH) e os meios
 * de pagamento dependem do que a propriedade habilitou. Com o `domKey` a
 * ajuda descreve o que ESTE hóspede permite fazer agora, não o catálogo
 * completo do sistema.
 *
 * Fatos: o painel busca o contexto pela ROTA, então as chaves com "#"
 * recebem os fatos de "/reservas" — hoje só bookings_checked_in e
 * bookings_arriving_today. O resto vem da base (pagamentos, WhatsApp).
 */

const METODO_DE_PAGAMENTO_LABEL: Record<string, string> = {
  pix: "PIX",
  credit: "cartão de crédito",
  debit: "cartão de débito",
  cash: "dinheiro",
  transfer: "transferência",
};

/** Meios de pagamento realmente liberados nas configurações desta propriedade. */
function meiosHabilitados(context: HelpContext): string[] {
  return Object.entries(context.payments.accepted_methods)
    .filter(([, habilitado]) => habilitado)
    .map(([metodo]) => METODO_DE_PAGAMENTO_LABEL[metodo] ?? metodo);
}

/**
 * Frase sobre a subconta Asaas — PIX, link privado e cartão cadastrado
 * passam por ela. Sem subconta aprovada esses três falham na hora de gerar
 * a cobrança, e é melhor o recepcionista saber disso antes do hóspede estar
 * de mala na mão.
 */
function estadoDoAsaas(context: HelpContext): string {
  const ambiente = context.payments.asaas_environment === "sandbox"
    ? " O ambiente está em sandbox: as cobranças são de teste, não entra dinheiro de verdade."
    : "";

  return context.payments.asaas_configured
    ? `subconta Asaas configurada nesta propriedade.${ambiente}`
    : `a subconta Asaas ainda não está configurada nesta propriedade — a cobrança falha ao ser gerada.${ambiente}`;
}

export const RESERVAS_AVANCADO_HELP: DetailedHelpMap = {
  "/reservas#detalhe.info": [
    {
      title: "O que esta aba faz",
      description:
        "É a ficha da reserva aberta: quem é o hóspede, qual quarto, quanto tempo fica e — o que mais importa no balcão — o que a recepção pode fazer com ela agora. A lista de ações não é fixa: o sistema só desenha o botão que o estado atual da reserva permite. Se o botão de check-in não está aí, é porque esta reserva não está no ponto de receber o hóspede.",
      items: [
        {
          label: "Por que os botões mudam de uma reserva para outra",
          explanation:
            "Check-in só aparece em reserva Confirmada — e mesmo aí o sistema recusa se a data de chegada ainda é futura ou se o quarto está fora de operação por manutenção. Check-out só aparece com o hóspede Hospedado. Reserva Pendente, Cancelada ou que já teve saída não mostra nenhum dos dois: sobra abrir a conta e reimprimir documentos.",
        },
        {
          label: "Faixa de números no topo (Noites, Total, Média, Origem)",
          domKey: "reserva.resumo_numeros",
          explanation:
            "É o valor contratado da reserva, não o que o hóspede deve. Consumo de frigobar, lavanderia e pagamentos já registrados não entram aqui — para saber quanto falta receber, use a aba Conta e pagamentos.",
        },
        {
          label: "Selos de pré-check-in e chegada",
          domKey: "reserva.selos_portal",
          explanation:
            "Vêm do portal que o hóspede recebe por e-mail/WhatsApp. \"Pré-check-in concluído\" significa que ele já preencheu os dados antes de chegar; \"Chegada informada\" que ele avisou que está a caminho ou na porta. Os dois são informação do hóspede: nenhum deles faz o check-in sozinho, a recepção ainda precisa confirmar.",
        },
        {
          label: "Selo \"Saída solicitada\"",
          domKey: "reserva.saida_solicitada",
          visual: { icon: LogOut, text: "Saída solicitada", tone: "outline" },
          explanation:
            "O hóspede pediu o check-out pelo portal. Só aparece nesse caso — e é o que troca o rótulo do botão de saída para \"Conferir e concluir saída\", lembrando a recepção de conferir a conta e o quarto antes de encerrar.",
        },
        {
          label: "Linha FNRH / SNRHos",
          domKey: "reserva.fnrh_status",
          explanation:
            "Situação do envio da Ficha Nacional de Registro de Hóspedes ao sistema oficial. Quatro estados: Pendente (ficha ainda não preenchida), Na fila (enviando), Enviado (aceito) e Erro no envio. O envio é assíncrono — sai da fila em segundo plano, então \"Na fila\" logo depois do check-in é normal.",
        },
        {
          label: "Fazer check-in / Confirmar entrada do hóspede",
          domKey: "reserva.checkin",
          visual: { icon: LogIn, text: "Fazer check-in", tone: "primary" },
          explanation:
            "O rótulo vira \"Confirmar entrada do hóspede\" quando ele já informou a chegada pelo portal. Abre um formulário de 3 passos: revisão da reserva, ficha FNRH (documento, profissão, nascimento, nacionalidade, procedência, destino, motivo e transporte — todos obrigatórios por exigência do Ministério do Turismo) e confirmação. Nada é gravado até o botão \"Confirmar Check-in\" do passo 3: fechar o modal antes disso não muda o status da reserva.",
          fact: (ctx) => {
            const chegando = ctx.screen.bookings_arriving_today;
            if (chegando === undefined) return null;
            return chegando === 0
              ? "nenhuma reserva confirmada com chegada marcada para hoje."
              : `${chegando} reserva(s) confirmada(s) com chegada hoje esperando check-in.`;
          },
        },
        {
          label: "Fazer check-out / Conferir e concluir saída",
          domKey: "reserva.checkout",
          visual: { icon: LogOut, text: "Fazer check-out", tone: "primary" },
          explanation:
            "O rótulo vira \"Conferir e concluir saída\" quando o hóspede já pediu a saída pelo portal. Antes de encerrar, o sistema consulta o saldo das contas da hospedagem: havendo valor em aberto, ele para e mostra o aviso de saldo pendente em vez de fechar a estadia com dívida. Concluído o check-out, o quarto vai automaticamente para Sujo no painel da governança.",
          fact: (ctx) => {
            const hospedados = ctx.screen.bookings_checked_in;
            if (hospedados === undefined) return null;
            return hospedados === 0
              ? "nenhum hóspede hospedado no momento."
              : `${hospedados} hóspede(s) hospedado(s) agora nesta propriedade.`;
          },
        },
        {
          label: "Aviso \"Saldo pendente\"",
          domKey: "reserva.saldo_pendente",
          visual: { icon: Receipt, text: "Saldo pendente", tone: "danger" },
          explanation:
            "Aparece no lugar do check-out quando ainda há valor a receber, e mostra quanto. \"Voltar\" cancela a saída; \"Abrir faturamento\" leva direto para a aba Conta e pagamentos para receber. Enquanto o saldo não zerar, o check-out não acontece.",
        },
        {
          label: "Abrir conta e pagamentos",
          domKey: "reserva.abrir_conta",
          visual: { icon: CreditCard, text: "Abrir conta e pagamentos", tone: "primary" },
          explanation:
            "Atalho para a outra aba deste mesmo modal — lançamentos, divisão entre as duas contas e recebimento. Este botão existe em qualquer status, inclusive depois do check-out, porque conta em aberto não desaparece com a saída do hóspede.",
        },
        {
          label: "Aviso vermelho de falha na FNRH",
          domKey: "reserva.alerta_fnrh",
          visual: { icon: AlertCircle, text: "Falha no envio da FNRH ao SNRHos", tone: "danger" },
          explanation:
            "O órgão oficial recusou a ficha e o motivo devolvido aparece logo abaixo. A hospedagem continua válida no Innsight, mas o registro legal não foi aceito — normalmente é dado do hóspede fora do padrão (documento, data de nascimento, nacionalidade). Corrija o cadastro do hóspede antes de reenviar, senão a fila vai recusar de novo.",
        },
        {
          label: "Reenviar FNRH",
          domKey: "reserva.reenviar_fnrh",
          visual: { icon: RefreshCw, text: "Reenviar FNRH", tone: "danger" },
          explanation:
            "Só existe quando o último envio falhou. Devolve a mesma ficha para a fila de envio — não refaz o check-in nem pede o formulário de novo. O selo volta para \"Na fila\" enquanto a tentativa acontece.",
        },
        {
          label: "Ver assinatura digital",
          domKey: "reserva.assinatura",
          visual: { icon: Download, text: "Ver assinatura digital", tone: "outline" },
          explanation:
            "Só existe se já houver uma FNRH registrada para esta reserva. Abre o comprovante de assinatura em outra aba. Se a ficha foi criada sem assinatura coletada, o sistema avisa que não encontrou o arquivo — o botão existir não garante que o hóspede assinou.",
        },
        {
          label: "Baixar Recibo",
          domKey: "reserva.recibo",
          visual: { icon: Download, text: "Baixar Recibo", tone: "outline" },
          explanation:
            "Aparece nos segundos seguintes a um check-out feito aqui neste modal. É o recibo em PDF da hospedagem, com os lançamentos das duas contas. Não é nota fiscal: a NFS-e é emitida no fechamento da conta.",
        },
      ],
    },
    {
      title: "Voucher e reenvio para o hóspede",
      items: [
        {
          label: "Botão Voucher (cabeçalho do modal)",
          domKey: "reserva.voucher",
          visual: { icon: Download, text: "Voucher", tone: "outline" },
          explanation:
            "Abre um menu com três opções: gerar o PDF da reserva, ou reenviar o link do portal do hóspede por e-mail ou por WhatsApp. Fica disponível em qualquer status.",
        },
        {
          label: "Abrir PDF",
          domKey: "reserva.voucher_pdf",
          visual: { icon: Download, text: "Abrir PDF", tone: "outline" },
          explanation:
            "Gera o voucher e abre numa aba nova para imprimir ou salvar. Se nada acontecer, provavelmente o navegador bloqueou a janela — libere pop-ups para o Innsight.",
        },
        {
          label: "Reenviar portal por e-mail",
          domKey: "reserva.voucher_email",
          visual: { icon: Mail, text: "Reenviar portal por e-mail", tone: "outline" },
          explanation:
            "Manda de novo o link do portal do hóspede para o e-mail que está na reserva. É o caminho quando o hóspede diz que não recebeu a confirmação ou perdeu o link do pré-check-in. O endereço usado é o do cadastro — se estiver errado, corrija o hóspede antes de reenviar.",
        },
        {
          label: "Reenviar portal pelo WhatsApp",
          domKey: "reserva.voucher_whatsapp",
          visual: { icon: MessageCircle, text: "Reenviar portal pelo WhatsApp", tone: "outline" },
          explanation:
            "Mesmo link, pelo WhatsApp, usando a instância configurada em Configurações > Integrações. Depende dessa integração estar ligada e conectada; sem isso o envio falha e o sistema avisa.",
          fact: (ctx) =>
            ctx.whatsapp.enabled
              ? `integração de WhatsApp ligada${ctx.whatsapp.instance ? ` (instância "${ctx.whatsapp.instance}")` : ""}.`
              : "a integração de WhatsApp está desligada nas configurações — o envio por esse canal não vai funcionar.",
        },
      ],
    },
  ],
  "/reservas#detalhe.folio": [
    {
      title: "O que esta aba faz",
      description:
        "É a conta da hospedagem, dividida em duas desde o começo: \"Conta 1 - Hospedagem\" (o que a empresa ou o titular contratou: diárias e taxas) e \"Conta 2 - Consumos e extras\" (o que o hóspede consumiu por conta própria). As duas são criadas automaticamente e são fechadas de forma independente — dá para faturar a diária na empresa e cobrar o frigobar do hóspede sem nenhuma gambiarra.",
      items: [
        {
          label: "Saldo em aberto (topo da aba)",
          domKey: "folio.saldo_total",
          explanation:
            "Soma das duas contas. É esse número que trava o check-out enquanto for positivo. Se aparecer negativo, o hóspede pagou mais do que devia — há crédito a devolver, não valor a receber.",
        },
        {
          label: "Selo \"Em aberto\" / \"Quitada\" no cabeçalho da conta",
          domKey: "folio.selo_situacao",
          explanation:
            "Uma conta vira Quitada quando o saldo dela zera ou quando o sistema a marca como paga/fechada. Enquanto estiver Em aberto, ela continua contando para o saldo do check-out — mesmo que a outra conta já esteja resolvida.",
        },
        {
          label: "Arrastar um item entre as contas",
          domKey: "folio.arrastar",
          explanation:
            "Segure o lançamento e solte no outro cartão. A transferência é gravada na hora; se o servidor recusar, o item volta sozinho para o lugar de origem e aparece o aviso de erro. É assim que se tira um consumo pessoal da conta que vai para a empresa.",
        },
        {
          label: "Adicionar item na conta",
          domKey: "folio.adicionar",
          visual: { icon: Plus, text: "Adicionar", tone: "outline" },
          explanation:
            "Lança um consumo extra naquela conta específica. Ao escolher um produto do estoque em vez de digitar a descrição livre, o lançamento dá baixa no estoque e registra a movimentação — e é recusado se não houver saldo suficiente do item. Descrição livre não mexe no estoque. O botão fica bloqueado quando a conta já teve NFS-e emitida.",
        },
        {
          label: "Imprimir extrato da conta",
          domKey: "folio.imprimir",
          visual: { icon: Printer, text: "Imprimir", tone: "outline" },
          explanation:
            "Abre uma janela de impressão com os lançamentos daquela conta. É documento operacional de conferência com o hóspede: não quita, não fecha e não vale como nota fiscal. Se nada abrir, o navegador bloqueou o pop-up.",
        },
        {
          label: "Estornar um lançamento",
          domKey: "folio.estornar",
          visual: { icon: Trash2, text: "Estornar transação", tone: "danger" },
          explanation:
            "A lixeira ao lado de cada linha remove aquele lançamento da conta e refaz o saldo. Conta já fechada ou com NFS-e emitida recusa a alteração — nesse caso é preciso reabrir a conta antes. Atenção: estornar um consumo que veio do estoque não devolve o produto ao estoque; esse ajuste é feito na tela de Estoque.",
        },
        {
          label: "Receber / Conta quitada",
          domKey: "folio.receber",
          visual: { icon: CreditCard, text: "Receber", tone: "primary" },
          explanation:
            "Abre o modal \"Receber e fechar conta\" daquela conta — cada uma recebe separadamente. O rótulo passa a \"Conta quitada\" e o botão desabilita quando o saldo já está zerado, então não dá para cobrar duas vezes por engano.",
        },
        {
          label: "Selo \"NFS-e emitida\"",
          domKey: "folio.nfse_emitida",
          explanation:
            "A nota fiscal de serviço daquela conta já foi emitida. A partir daí a conta fica travada para novos lançamentos e para estorno — o caminho é cancelar a nota reabrindo a conta, para não gerar tributação duplicada.",
        },
        {
          label: "Selo \"NFS-e falhou\"",
          domKey: "folio.nfse_falhou",
          explanation:
            "A prefeitura ou o emissor recusou a nota daquela conta. O dinheiro pode já ter sido recebido — o que falhou foi só o documento fiscal. Resolva pelo módulo financeiro antes do fechamento do mês.",
        },
      ],
    },
    {
      title: "Receber e fechar conta",
      description:
        "O modal mostra o saldo daquela conta específica e só lista os meios de pagamento que a propriedade habilitou em Configurações. Meio que não aparece aqui não é bug: foi desligado nas configurações.",
      items: [
        {
          label: "Quais meios estão liberados nesta propriedade",
          explanation:
            "A lista de formas de pagamento sai das configurações do hotel, não de uma lista fixa do sistema. Desligar um meio lá some com o botão aqui e o servidor também recusa o registro — o bloqueio não é só visual.",
          fact: (ctx) => {
            const metodos = ctx.payments.accepted_methods;
            if (Object.keys(metodos).length === 0) return null;
            const ligados = meiosHabilitados(ctx);
            return ligados.length === 0
              ? "nenhum meio de pagamento habilitado — o fechamento fica bloqueado até liberar ao menos um em Configurações."
              : `habilitados hoje: ${ligados.join(", ")}.`;
          },
        },
        {
          label: "PIX na recepção",
          domKey: "folio.pagamento.pix",
          visual: { icon: QrCode, text: "PIX na recepção", tone: "outline" },
          explanation:
            "Gera um QR Code na tela para o hóspede escanear ali mesmo. Enquanto o QR estiver aberto o sistema fica consultando a cobrança sozinho: assim que o pagamento cai, a conta é atualizada sem ninguém precisar clicar em nada.",
          fact: (ctx) => estadoDoAsaas(ctx),
        },
        {
          label: "Link privado",
          domKey: "folio.pagamento.private_link",
          visual: { icon: Send, text: "Link privado", tone: "outline" },
          explanation:
            "Cria um link do checkout seguro do Innsight para o hóspede pagar no próprio celular — o recepcionista não vê nem digita dados de cartão. O link é o portal desta reserva: se a reserva ainda não tiver portal gerado, o sistema avisa e não cria a cobrança.",
        },
        {
          label: "Cartão cadastrado",
          domKey: "folio.pagamento.stored_card",
          visual: { icon: CreditCard, text: "Cartão cadastrado", tone: "outline" },
          explanation:
            "Cobra uma parte ou todo o saldo no cartão que o hóspede já tokenizou como garantia (normalmente no portal). O sistema impede cobrar acima do saldo disponível e limita a hospedagem a dois meios de pagamento. Se não houver cartão salvo, use o link privado ou outro meio autorizado.",
        },
        {
          label: "Maquininha local",
          domKey: "folio.pagamento.terminal",
          visual: { icon: Smartphone, text: "Maquininha local", tone: "outline" },
          explanation:
            "Registra no sistema uma cobrança que você já passou na maquininha física. O Innsight não conversa com a maquininha: é lançamento manual. O campo de NSU/autorização é opcional, mas é o que permite reconciliar depois com o extrato da adquirente.",
        },
        {
          label: "Dinheiro",
          domKey: "folio.pagamento.cash",
          visual: { icon: Banknote, text: "Dinheiro", tone: "outline" },
          explanation:
            "Informe quanto o hóspede entregou; o sistema calcula o troco e registra como pagamento no máximo o saldo da conta. Valor menor que o saldo é aceito: registra um pagamento parcial e a conta segue em aberto pela diferença.",
        },
        {
          label: "Valor recebido em dinheiro",
          domKey: "folio.pagamento.valor_dinheiro",
          explanation:
            "É o valor que entrou na gaveta, não o valor da dívida. Se for maior que o saldo, o excedente vira troco e só o saldo é baixado da conta.",
        },
        {
          label: "Valor cobrado na maquininha",
          domKey: "folio.pagamento.valor_maquininha",
          explanation:
            "Precisa bater com o que foi realmente passado no cartão. O sistema recusa valor acima do saldo da conta — para dividir a cobrança, registre um valor por vez.",
        },
        {
          label: "NSU / autorização",
          domKey: "folio.pagamento.nsu",
          explanation:
            "Número que a maquininha imprime no comprovante. Opcional, mas é ele que aparece na descrição do lançamento e liga o pagamento do Innsight ao da adquirente na hora de fechar o caixa.",
        },
        {
          label: "Botão de confirmar (Gerar PIX / Criar link / Registrar pagamento)",
          domKey: "folio.pagamento.confirmar",
          explanation:
            "O rótulo muda conforme o meio escolhido. Fica desabilitado quando a conta não tem saldo a receber ou quando nenhum meio de pagamento está liberado.",
        },
        {
          label: "Aviso de nenhum meio liberado",
          domKey: "folio.pagamento.sem_meios",
          explanation:
            "A propriedade desligou todas as formas de pagamento em Configurações. Enquanto estiver assim, nenhuma conta pode ser recebida por aqui — e, sem receber, o check-out fica travado pelo saldo.",
        },
      ],
    },
  ],
  "/hospedes": [
    {
      title: "O que esta tela faz",
      description:
        "É o cadastro único de hóspedes da propriedade. O hóspede existe uma vez só, não uma vez por reserva — é isso que mantém histórico de estadias, preferências e alergias no mesmo lugar em vez de espalhados por reservas soltas.",
      items: [
        {
          label: "Como o sistema evita cadastro duplicado",
          explanation:
            "Ao criar uma reserva, se o e-mail informado já tiver cadastro, o sistema reaproveita aquele hóspede em vez de criar outro. A comparação é só por e-mail: telefone e CPF iguais não são suficientes. Dois e-mails diferentes para a mesma pessoa geram dois cadastros e dois históricos separados.",
          fact: (ctx) => {
            const total = ctx.screen.guests_total;
            if (total === undefined) return null;
            return total === 0 ? "nenhum hóspede cadastrado ainda." : `${total} hóspede(s) na base.`;
          },
        },
        {
          label: "Busca",
          domKey: "hospedes.busca",
          explanation:
            "Procura por nome, CPF ou e-mail e consulta o servidor enquanto você digita (com uma pequena pausa entre as teclas). A lista já vem limitada — usar a busca é o jeito de chegar a um hóspede que não está nas primeiras linhas.",
        },
        {
          label: "Novo Hóspede",
          domKey: "hospedes.novo",
          visual: { icon: Plus, text: "Novo Hóspede", tone: "primary" },
          explanation:
            "Cadastra alguém sem vincular a uma reserva — útil para cliente corporativo recorrente ou pré-cadastro de grupo. Só nome e e-mail são obrigatórios; documento e endereço podem ser completados no check-in.",
        },
        {
          label: "E-mail no cadastro novo",
          domKey: "hospede.novo_email",
          explanation:
            "É a chave de reaproveitamento do cadastro. Um e-mail digitado errado aqui vira um hóspede duplicado na próxima reserva, com histórico partido ao meio — vale conferir antes de salvar.",
        },
        {
          label: "CEP no cadastro novo",
          domKey: "hospede.novo_cep",
          explanation:
            "Ao sair do campo, o sistema busca o endereço e preenche rua, cidade e estado sozinho. Se o CEP não for encontrado, os campos ficam livres para digitação manual.",
        },
        {
          label: "Coluna Tags",
          domKey: "hospedes.coluna_tags",
          explanation:
            "Marcadores do perfil: VIP, Corporativo, Fidelizado, Alérgico, Aniversariante, Grupo e Blacklist. A lista mostra as duas primeiras e um contador com o restante.",
        },
        {
          label: "Coluna FNRH",
          domKey: "hospedes.coluna_fnrh",
          explanation:
            "Situação do último envio da ficha desse hóspede ao SNRHos: Pendente, Na fila, Enviado ou Erro no envio. Serve para varrer a base atrás de fichas recusadas antes que virem problema com a fiscalização.",
        },
        {
          label: "Coluna Valor do hóspede",
          domKey: "hospedes.coluna_valor",
          explanation:
            "Receita total já gerada por esse hóspede, abreviada em milhares. É o número usado para decidir cortesia e upgrade — não é saldo em aberto de nenhuma reserva.",
        },
      ],
    },
    {
      title: "Perfil do hóspede",
      description:
        "Abre ao clicar numa linha da lista. A lista traz e-mail e telefone mascarados; ao abrir o perfil, o sistema busca os dados completos do cadastro.",
      items: [
        {
          label: "Botão Tag",
          domKey: "hospede.adicionar_tag",
          visual: { icon: Tag, text: "Tag", tone: "outline" },
          explanation:
            "Abre a lista de marcadores ainda não aplicados. Cada tag já aplicada tem um \"x\" ao lado para remover. A gravação é imediata e, se falhar, a tag volta ao estado anterior.",
        },
        {
          label: "Lista de tags disponíveis",
          domKey: "hospede.tags_disponiveis",
          explanation:
            "Mostra só as tags que esse hóspede ainda não tem. Elas ficam indisponíveis por um instante enquanto o perfil completo termina de carregar — é proteção para não sobrescrever as preferências já salvas.",
        },
        {
          label: "Selo \"Bloqueado\"",
          domKey: "hospede.bloqueado",
          visual: { icon: Ban, text: "Bloqueado", tone: "danger" },
          explanation:
            "Aparece quando o hóspede tem a tag Blacklist, e a linha dele fica esmaecida na lista. É um sinalizador para a equipe decidir: o sistema não impede automaticamente uma nova reserva no nome dele.",
        },
        {
          label: "Valor do relacionamento",
          domKey: "hospede.indicadores",
          explanation:
            "Gasto total, número de estadias e média de noites. O gasto total vem consolidado do cadastro; estadias e média são recalculadas a partir das reservas carregadas ao abrir o perfil. O indicador de No-Show ainda não é calculado — hoje ele mostra 0% para todo mundo, não use como critério.",
        },
        {
          label: "Aba Estadias",
          domKey: "hospede.estadias",
          explanation:
            "Todas as reservas desse hóspede com período, quarto e valor — inclui as futuras e a que está em andamento, não só as encerradas. Os rótulos Cancelada e No-Show são confiáveis; qualquer outro estado (inclusive reserva que ainda nem começou) aparece rotulado como Concluída, então confira a data antes de concluir alguma coisa.",
        },
        {
          label: "Preferências informadas pelo hóspede",
          domKey: "hospede.salvar_preferencias",
          visual: { icon: Save, text: "Salvar preferências", tone: "outline" },
          explanation:
            "Alergias, restrições alimentares, acessibilidade e preferências de quarto. Esses campos também são preenchidos pelo próprio hóspede no portal da reserva, então o que ele escreveu lá aparece aqui. Só grava ao clicar em Salvar preferências.",
        },
        {
          label: "Notas e Preferências (texto livre)",
          domKey: "hospede.notas",
          explanation:
            "Bloco de observações internas da equipe, separado das preferências estruturadas acima. Clique em Editar, escreva, clique em Salvar — sai do modo de edição só depois de gravar.",
        },
      ],
    },
  ],
  "/portais-dos-hospedes": [
    {
      title: "O que esta tela faz",
      description:
        "Mostra, numa lista só, em que ponto do autoatendimento cada hóspede está: quem já preencheu o pré-check-in, quem avisou que chegou e ainda espera a recepção, e quem nem abriu o portal. É a fila que a recepção usa para saber quem já pode subir direto para o quarto e quem ainda precisa de balcão.",
      items: [
        {
          label: "Coluna Situação",
          explanation:
            "Status da reserva em si: Pendente, Confirmada, Hospedado, Concluída (saída já feita), Cancelada ou Não compareceu. É o mesmo estado da reserva no PMS — esta tela não muda status, ela só acompanha.",
        },
        {
          label: "Selo \"Chegou · aguarda recepção\"",
          visual: { text: "Chegou · aguarda recepção", tone: "outline" },
          explanation:
            "O hóspede apertou \"cheguei\" no portal e ninguém confirmou ainda. É a fila de atendimento do balcão: enquanto esse selo estiver aí, alguém está esperando. Ele some quando a recepção confirma a entrada pelo detalhe da reserva.",
        },
        {
          label: "Selo \"Pré-check-in concluído\"",
          visual: { text: "Pré-check-in concluído", tone: "outline" },
          explanation:
            "O hóspede já preencheu os dados no portal antes de chegar. Os dois selos nunca aparecem juntos: enquanto ele estiver na fila de chegada, o selo de chegada assume o lugar deste. Pré-check-in preenchido não é check-in feito — a ficha ainda precisa ser confirmada pela recepção.",
        },
        {
          label: "Abrir portal",
          visual: { icon: ExternalLink, text: "Abrir portal", tone: "outline" },
          explanation:
            "Abre em outra aba exatamente o link que o hóspede recebeu — mesma tela que ele vê, sem cópia nem simulação. Serve para acompanhar ao telefone o que ele está vendo.",
        },
        {
          label: "\"Portal indisponível\"",
          explanation:
            "Aparece quando a reserva nunca gerou link de portal. Sem link, não há pré-check-in nem pagamento pelo celular para essa reserva — gere o portal reenviando pelo botão Voucher no detalhe da reserva. Atenção: um link já expirado continua aparecendo como \"Abrir portal\" aqui; a validade não é conferida nesta lista.",
        },
        {
          label: "Busca e Atualizar",
          visual: { icon: RefreshCw, text: "Atualizar", tone: "outline" },
          explanation:
            "A busca aceita nome do hóspede ou código da reserva (não busca por e-mail) e só consulta ao enviar o formulário. A lista não se atualiza sozinha — em dia de movimento, use Atualizar para ver quem chegou desde a última consulta.",
        },
        {
          label: "Por que uma reserva pode não estar na lista",
          explanation:
            "Esta tela não mostra a base inteira: traz as reservas mais recentes por data de chegada, num teto de algumas dezenas de linhas. Reserva antiga não aparece por rolagem — só pela busca por nome ou código.",
        },
      ],
    },
  ],
};
